/**
 * Tabela unificada de sessões do fluxo (estilo Typebot)
 * Recebe dados já paginados da RPC get_flow_results_paginated
 * Inclui colunas dinâmicas por node interativo com controle de visibilidade
 * Barra de filtros completa: inbox, período, trigger_type, status, busca, origem
 * 
 * @release v4.1.0 - 2026-10-02 - Botão "Exportar" passa a usar o controle padrão de
 *          exportação (ExportMenuButton, fonte 'flow_sessions'): permissão por perfil,
 *          todas as sessões do filtro, envio por e-mail e auditoria. Removido o
 *          export caseiro CSV/XLS da página atual.
 * @release v4.0.0 - 2026-03-08
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link2, MessageSquare, MessageCircle, Phone, Columns3, Eye, EyeOff, Save, Trash2, FolderOpen, Pencil, FileText, Search, X, Inbox, RefreshCw } from 'lucide-react';
import { CrmInboxFilter } from '@/components/crm/filters';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { showToast } from '@/lib/toast';
import { logger } from '@/lib/logger';
import { FlowSessionLogsModal } from './FlowSessionLogsModal';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import type { PaginatedFlowSession, FlowSessionFilters } from '@/hooks/useFlowResultsPaginated';
import { ExportMenuButton } from '@/components/export/ExportMenuButton';
import type { FlowSessionsExportParams } from '@/lib/export/sources/flowSessionsSource';

/** Formato de coluna dinâmica extraída dos nodes do fluxo */
export interface NodeColumn {
  /** ID do node no fluxo */
  nodeId: string;
  /** Label legível do node (nome da pergunta/input) */
  label: string;
  /** Tipo do node (question_buttons, input_data, etc.) */
  nodeType: string;
}

/** Mapeia trigger_type para label legível */
const TRIGGER_LABELS: Record<string, string> = {
  link: 'Link',
  keyword: 'Keyword',
  first_message: '1ª Mensagem',
  scheduled: 'Agendado',
  inactivity: 'Inatividade',
  event: 'Evento',
  webhook: 'Webhook',
  phase: 'Fase',
  contact_tag: 'Tag Contato',
  department: 'Departamento',
  active_conversation: 'Conversa Ativa',
  any_message: 'Qualquer Msg',
  crm_filter: 'Filtro CRM',
  welcome_flow: 'Boas-vindas',
  contract_event: 'Contrato',
};

/** Mapeia status da execução para configuração de badge */
const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  completed: { label: 'Concluído', variant: 'default' },
  running: { label: 'Em andamento', variant: 'secondary' },
  failed: { label: 'Falhou', variant: 'destructive' },
  paused: { label: 'Pausado', variant: 'outline' },
  cancelled: { label: 'Cancelado', variant: 'outline' },
  waiting_input: { label: 'Aguardando', variant: 'secondary' },
  waiting_delay: { label: 'Aguardando', variant: 'secondary' },
  in_progress: { label: 'Em andamento', variant: 'secondary' },
};

/** Calcula duração entre duas datas em formato legível */
function calcDuration(start: string, end: string | null): string {
  if (!end) return '-';
  const diffMs = new Date(end).getTime() - new Date(start).getTime();
  if (diffMs < 0) return '-';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

/** Quantidade padrão de colunas de nodes visíveis inicialmente */
const DEFAULT_VISIBLE_COLUMNS = 15;

/** Interface de preset de colunas salvo no banco */
interface ColumnPreset {
  id: string;
  name: string;
  visible_node_ids: string[];
}

interface FlowResultsSessionsTableProps {
  /** Sessões já paginadas retornadas pela RPC */
  sessions: PaginatedFlowSession[];
  /** Colunas dinâmicas extraídas dos nodes interativos do fluxo */
  nodeColumns?: NodeColumn[];
  /** ID do fluxo para salvar presets de colunas */
  flowId?: string;
  /** Total de itens (server-side) */
  totalCount: number;
  /** Total de páginas */
  totalPages: number;
  /** Página atual */
  currentPage: number;
  /** Tamanho da página */
  pageSize: number;
  /** Callback quando página muda */
  onPageChange: (page: number) => void;
  /** Callback quando tamanho da página muda */
  onPageSizeChange: (size: number) => void;
  /** Filtros ativos */
  filters: FlowSessionFilters;
  /** Callback quando filtros mudam */
  onFiltersChange: (filters: FlowSessionFilters) => void;
  /** Se está carregando dados */
  isLoading?: boolean;
  /** Se está fazendo fetch em background */
  isFetching?: boolean;
  /** Callback para atualizar dados */
  onRefresh?: () => void;
  /** Se está atualizando dados */
  isRefreshing?: boolean;
}

export function FlowResultsSessionsTable({
  sessions,
  nodeColumns = [],
  flowId,
  totalCount,
  totalPages,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  filters,
  onFiltersChange,
  isLoading,
  isFetching,
  onRefresh,
  isRefreshing,
}: FlowResultsSessionsTableProps) {
  const queryClient = useQueryClient();

  // Estado de visibilidade das colunas dinâmicas (set de nodeIds visíveis)
  const [visibleNodeIds, setVisibleNodeIds] = useState<Set<string>>(new Set());
  // Estado do preset ativo selecionado
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  // Estado para controle de criação de novo preset
  const [isCreatingPreset, setIsCreatingPreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  // Estado do modal de logs da sessão
  const [logsSessionId, setLogsSessionId] = useState<string | null>(null);
  // Estado do modal de chat da sessão
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);
  // Estado do input de busca (com debounce)
  const [searchInput, setSearchInput] = useState('');

  // Debounce para busca livre
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (filters.search || '')) {
        onFiltersChange({ ...filters, search: searchInput || null });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // Busca presets salvos do fluxo atual
  // =========================================================================
  const { data: presets = [] } = useQuery({
    queryKey: ['flow-column-presets', flowId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('flow_column_presets')
        .select('id, name, visible_node_ids')
        .eq('flow_id', flowId)
        .order('created_at', { ascending: true });
      if (error) {
        logger.error('[FlowResultsSessionsTable] Erro ao buscar presets:', String(error.message));
        throw error;
      }
      return (data || []) as ColumnPreset[];
    },
    enabled: !!flowId,
  });

  // =========================================================================
  // Mutation para salvar novo preset
  // =========================================================================
  const savePresetMutation = useMutation({
    mutationFn: async (name: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');
      const { data: tenantId } = await supabase.rpc('get_current_user_tenant_id' as any);
      if (!tenantId) throw new Error('Tenant não encontrado');
      const { error } = await (supabase as any)
        .from('flow_column_presets')
        .insert({
          flow_id: flowId,
          tenant_id: tenantId,
          user_id: user.id,
          name,
          visible_node_ids: Array.from(visibleNodeIds),
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flow-column-presets', flowId] });
      showToast.success('Preset salvo com sucesso!');
      setIsCreatingPreset(false);
      setNewPresetName('');
    },
    onError: (err: Error) => {
      logger.error('[FlowResultsSessionsTable] Erro ao salvar preset:', err.message);
      showToast.error('Erro ao salvar preset', err.message);
    },
  });

  // =========================================================================
  // Mutation para excluir preset
  // =========================================================================
  const deletePresetMutation = useMutation({
    mutationFn: async (presetId: string) => {
      const { error } = await (supabase as any)
        .from('flow_column_presets')
        .delete()
        .eq('id', presetId);
      if (error) throw error;
    },
    onSuccess: (_data: unknown, deletedId: string) => {
      queryClient.invalidateQueries({ queryKey: ['flow-column-presets', flowId] });
      if (activePresetId === deletedId) setActivePresetId(null);
      showToast.success('Preset excluído');
    },
    onError: (err: Error) => {
      logger.error('[FlowResultsSessionsTable] Erro ao excluir preset:', err.message);
      showToast.error('Erro ao excluir preset', err.message);
    },
  });

  // =========================================================================
  // Mutation para atualizar preset existente com seleção atual
  // =========================================================================
  const updatePresetMutation = useMutation({
    mutationFn: async (presetId: string) => {
      const { error } = await (supabase as any)
        .from('flow_column_presets')
        .update({ visible_node_ids: Array.from(visibleNodeIds) })
        .eq('id', presetId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flow-column-presets', flowId] });
      showToast.success('Preset atualizado com sucesso!');
    },
    onError: (err: Error) => {
      logger.error('[FlowResultsSessionsTable] Erro ao atualizar preset:', err.message);
      showToast.error('Erro ao atualizar preset', err.message);
    },
  });

  // Sincroniza colunas visíveis quando nodeColumns carrega
  useEffect(() => {
    if (nodeColumns.length > 0 && !activePresetId) {
      setVisibleNodeIds(new Set(nodeColumns.slice(0, DEFAULT_VISIBLE_COLUMNS).map(c => c.nodeId)));
    }
  }, [nodeColumns, activePresetId]);

  // Colunas dinâmicas ativas (filtradas pela visibilidade)
  const activeNodeColumns = useMemo(() => {
    return nodeColumns.filter(c => visibleNodeIds.has(c.nodeId));
  }, [nodeColumns, visibleNodeIds]);

  // Toggle de visibilidade de uma coluna individual
  const toggleColumnVisibility = (nodeId: string) => {
    setVisibleNodeIds(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const showAllColumns = () => setVisibleNodeIds(new Set(nodeColumns.map(c => c.nodeId)));
  const hideAllColumns = () => { setVisibleNodeIds(new Set()); setActivePresetId(null); };

  const applyPreset = (presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      setVisibleNodeIds(new Set(preset.visible_node_ids));
      setActivePresetId(presetId);
    }
  };

  const handleSavePreset = () => {
    const trimmed = newPresetName.trim();
    if (!trimmed) return;
    savePresetMutation.mutate(trimmed);
  };

  // Handler de mudança de filtro individual
  const updateFilter = useCallback((key: keyof FlowSessionFilters, value: string | null) => {
    onFiltersChange({ ...filters, [key]: value === 'all' ? null : value });
  }, [filters, onFiltersChange]);

  // Limpar todos os filtros
  const clearFilters = useCallback(() => {
    setSearchInput('');
    onFiltersChange({});
  }, [onFiltersChange]);

  // Verifica se há filtros ativos
  const hasActiveFilters = !!(filters.source || filters.triggerType || filters.status || filters.inboxId || filters.search || filters.startDate);

  // =========================================================================
  // Exportação: movida para o controle padrão (ExportMenuButton / fonte flow_sessions)

  // Largura mínima dinâmica baseada no número de colunas ativas
  const tableMinWidth = 1400 + activeNodeColumns.length * 150;
  const totalColSpan = 13 + activeNodeColumns.length;

  /** Cores da borda lateral por tipo de origem */
  const SOURCE_BORDER_COLORS: Record<string, string> = {
    link: 'border-l-sky-500',
    keyword: 'border-l-violet-500',
    first_message: 'border-l-emerald-500',
    any_message: 'border-l-emerald-500',
    event: 'border-l-amber-500',
    contract_event: 'border-l-amber-500',
    webhook: 'border-l-orange-500',
    scheduled: 'border-l-blue-500',
    inactivity: 'border-l-rose-500',
    welcome_flow: 'border-l-teal-500',
    contact_tag: 'border-l-pink-500',
    department: 'border-l-indigo-500',
    phase: 'border-l-cyan-500',
    crm_filter: 'border-l-fuchsia-500',
    active_conversation: 'border-l-lime-500',
  };

  return (
    <div className="rounded-lg border border-border/50 bg-card overflow-hidden">
      {/* Barra de filtros compacta com título integrado */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Título da seção */}
        <h2 className="text-base font-semibold mr-1">Resultados</h2>
        {nodeColumns.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="xs" className="gap-1.5 h-8">
                <Columns3 className="h-3.5 w-3.5" />
                Colunas
                <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">
                  {activeNodeColumns.length}/{nodeColumns.length}
                </Badge>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 max-h-[28rem] overflow-y-auto p-3">
              <div className="space-y-3">
                {/* Seção de Presets Salvos */}
                {flowId && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <FolderOpen className="h-3.5 w-3.5" />
                      Presets Salvos
                    </span>
                    {presets.length > 0 ? (
                      <div className="space-y-1">
                        {presets.map((preset) => (
                          <div
                            key={preset.id}
                            className={`flex items-center justify-between rounded px-2 py-1.5 cursor-pointer transition-colors text-sm ${
                              activePresetId === preset.id
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-accent/50'
                            }`}
                            onClick={() => applyPreset(preset.id)}
                          >
                            <span className="truncate flex-1">{preset.name}</span>
                            <div className="flex items-center gap-1 shrink-0">
                              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                {preset.visible_node_ids.length}
                              </Badge>
                              {activePresetId === preset.id && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        className="h-6 w-6"
                                        onClick={(e) => { e.stopPropagation(); updatePresetMutation.mutate(preset.id); }}
                                        disabled={updatePresetMutation.isPending}
                                      >
                                        <Pencil className="h-3 w-3 text-primary" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Atualizar preset com seleção atual</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="h-6 w-6"
                                onClick={(e) => { e.stopPropagation(); deletePresetMutation.mutate(preset.id); }}
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">Nenhum preset salvo</p>
                    )}
                    {isCreatingPreset ? (
                      <div className="flex gap-1.5">
                        <Input
                          placeholder="Nome do preset..."
                          value={newPresetName}
                          onChange={(e) => setNewPresetName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                          className="h-7 text-xs"
                          autoFocus
                        />
                        <Button
                          variant="default"
                          size="xs"
                          className="h-7 px-2 shrink-0"
                          onClick={handleSavePreset}
                          disabled={!newPresetName.trim() || savePresetMutation.isPending}
                        >
                          <Save className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="xs"
                        className="w-full text-xs h-7"
                        onClick={() => setIsCreatingPreset(true)}
                      >
                        <Save className="h-3 w-3 mr-1" />
                        Salvar seleção atual
                      </Button>
                    )}
                    <Separator />
                  </div>
                )}

                {/* Cabeçalho do popover de colunas */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Colunas de Nodes</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="xs" onClick={showAllColumns} className="text-xs h-6 px-2">
                      <Eye className="h-3 w-3 mr-1" />
                      Todas
                    </Button>
                    <Button variant="ghost" size="xs" onClick={hideAllColumns} className="text-xs h-6 px-2">
                      <EyeOff className="h-3 w-3 mr-1" />
                      Nenhuma
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {nodeColumns.map((col) => (
                    <label
                      key={col.nodeId}
                      className="flex items-center gap-2 cursor-pointer hover:bg-accent/50 rounded px-1.5 py-1 transition-colors"
                    >
                      <Checkbox
                        checked={visibleNodeIds.has(col.nodeId)}
                        onCheckedChange={() => toggleColumnVisibility(col.nodeId)}
                      />
                      <span className="text-sm truncate flex-1" title={col.label}>{col.label}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{col.nodeType.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
                {nodeColumns.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    Nenhum node interativo no fluxo
                  </p>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Filtro por origem (Inbox / Link Externo) */}
        <Select
          value={filters.source || 'all'}
          onValueChange={(v) => updateFilter('source', v)}
        >
          <SelectTrigger className="w-[140px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas origens</SelectItem>
            <SelectItem value="whatsapp">Inbox</SelectItem>
            <SelectItem value="link">Link Externo</SelectItem>
          </SelectContent>
        </Select>

        {/* Filtro por Inbox */}
        <CrmInboxFilter
          value={filters.inboxId || null}
          onChange={(v) => updateFilter('inboxId', v)}
          className="h-8"
        />

        {/* Filtro por trigger_type */}
        <Select
          value={filters.triggerType || 'all'}
          onValueChange={(v) => updateFilter('triggerType', v)}
        >
          <SelectTrigger className="w-[140px] h-8">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos tipos</SelectItem>
            <SelectItem value="keyword">Keyword</SelectItem>
            <SelectItem value="first_message">1ª Mensagem</SelectItem>
            <SelectItem value="any_message">Qualquer Msg</SelectItem>
            <SelectItem value="event">Evento</SelectItem>
            <SelectItem value="contract_event">Contrato</SelectItem>
            <SelectItem value="webhook">Webhook</SelectItem>
            <SelectItem value="scheduled">Agendado</SelectItem>
            <SelectItem value="inactivity">Inatividade</SelectItem>
            <SelectItem value="welcome_flow">Boas-vindas</SelectItem>
            <SelectItem value="contact_tag">Tag Contato</SelectItem>
            <SelectItem value="department">Departamento</SelectItem>
            <SelectItem value="phase">Fase</SelectItem>
            <SelectItem value="link">Link</SelectItem>
          </SelectContent>
        </Select>

        {/* Filtro por status */}
        <Select
          value={filters.status || 'all'}
          onValueChange={(v) => updateFilter('status', v)}
        >
          <SelectTrigger className="w-[140px] h-8">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="completed">Concluído</SelectItem>
            <SelectItem value="running">Em andamento</SelectItem>
            <SelectItem value="failed">Falhou</SelectItem>
            <SelectItem value="paused">Pausado</SelectItem>
            <SelectItem value="cancelled">Cancelado</SelectItem>
            <SelectItem value="waiting_input">Aguardando</SelectItem>
            <SelectItem value="in_progress">Em progresso</SelectItem>
          </SelectContent>
        </Select>

        {/* Input de busca livre com debounce */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar contato, telefone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-8 w-[200px] pl-7 text-xs"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2"
            >
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>

        {/* Limpar filtros */}
        {hasActiveFilters && (
          <Button variant="ghost" size="xs" className="h-8 text-xs" onClick={clearFilters}>
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}

        {/* Contador de resultados e botão atualizar à direita */}
        <div className="ml-auto flex items-center gap-2">
          {isFetching && !isLoading && (
            <span className="text-xs text-muted-foreground animate-pulse">Atualizando...</span>
          )}
          <Badge variant="secondary" className="text-xs">
            {totalCount} sessões
          </Badge>
          {onRefresh && (
            <Button
              variant="outline"
              size="xs"
              className="h-8"
              onClick={onRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          )}

          {/* v2: exportação no controle padrão (permissão, auditoria, e-mail) */}
          {flowId && (
            <ExportMenuButton
              variant="button"
              sourceId="flow_sessions"
              fileNameHint={filters.nodeId ? nodeColumns.find((c) => c.nodeId === filters.nodeId)?.label : undefined}
              params={{
                flowId,
                nodeId: filters.nodeId || null,
                nodeLabel: filters.nodeId ? nodeColumns.find((c) => c.nodeId === filters.nodeId)?.label || null : null,
                startDate: filters.startDate || null,
                endDate: filters.endDate || null,
                inboxId: filters.inboxId || null,
                triggerType: filters.triggerType || null,
                status: filters.status || null,
                source: filters.source || null,
                search: filters.search || null,
                nodeColumns: nodeColumns.map((c) => ({ nodeId: c.nodeId, label: c.label })),
              } satisfies FlowSessionsExportParams}
            />
          )}
        </div>
      </div>

      {/* Tabela full-width */}
      <div className="pb-2">
        <ScrollIndicators showArrows showGradients scrollAmount={300}>
          <div className="max-h-[500px] overflow-auto" data-scroll-viewport="true">
              <Table style={{ minWidth: `${tableMinWidth}px` }}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[110px]">Origem</TableHead>
                    <TableHead className="min-w-[110px]">Tipo</TableHead>
                    <TableHead className="min-w-[130px]">Inbox</TableHead>
                    <TableHead className="min-w-[160px]">Gatilho</TableHead>
                    <TableHead className="min-w-[160px]">Contato / Visitante</TableHead>
                    <TableHead className="min-w-[150px]">Telefone</TableHead>
                    <TableHead className="min-w-[110px]">Status</TableHead>
                    <TableHead className="min-w-[140px]">Iniciado em</TableHead>
                    <TableHead className="min-w-[90px]">Duração</TableHead>
                    <TableHead className="min-w-[120px]">Último Node</TableHead>
                    <TableHead className="min-w-[220px]">Variáveis</TableHead>
                    <TableHead className="min-w-[100px]">Logs</TableHead>
                    <TableHead className="min-w-[100px]">Chat</TableHead>
                    {activeNodeColumns.map((col) => (
                      <TableHead key={col.nodeId} className="min-w-[150px]" title={`${col.label} (${col.nodeType})`}>
                        <span className="truncate block max-w-[140px]">{col.label}</span>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={totalColSpan} className="text-center text-muted-foreground py-8">
                        Carregando sessões...
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && sessions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={totalColSpan} className="text-center text-muted-foreground py-8">
                        Nenhuma sessão encontrada
                      </TableCell>
                    </TableRow>
                  )}
                  {sessions.map((session) => {
                    const statusCfg = STATUS_CONFIG[session.status] || { label: session.status, variant: 'outline' as const };
                    const triggerLabel = TRIGGER_LABELS[session.trigger_type] || session.trigger_type;
                    const stepResponses = (session.step_responses || {}) as Record<string, string>;
                    const context = session.context as Record<string, unknown> | null;

                    return (
                      <TableRow key={session.id} className={`border-l-4 ${SOURCE_BORDER_COLORS[session.trigger_type] || 'border-l-muted'}`}>
                        {/* Coluna Origem (Inbox / Link Externo) */}
                        <TableCell>
                          <Badge variant="outline" className="flex items-center gap-1 w-fit text-xs">
                            {session.source === 'link' ? (
                              <Link2 className="h-3 w-3" />
                            ) : (
                              <Inbox className="h-3 w-3" />
                            )}
                            {session.source === 'link' ? 'Link Externo' : 'Inbox'}
                          </Badge>
                        </TableCell>

                        {/* Coluna Tipo (trigger_type) */}
                        <TableCell>
                          <Badge variant="outline" className="flex items-center gap-1 w-fit text-xs">
                            {session.source === 'link' ? (
                              <Link2 className="h-3 w-3" />
                            ) : (
                              <MessageSquare className="h-3 w-3" />
                            )}
                            {triggerLabel}
                          </Badge>
                        </TableCell>

                        {/* Coluna Inbox */}
                        <TableCell className="text-sm">
                          {session.inbox_name ? (
                            <span className="truncate block max-w-[120px]" title={session.inbox_name}>
                              {session.inbox_name}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>

                        {/* Coluna Gatilho */}
                        <TableCell className="text-sm">
                          {session.trigger_detail ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="truncate block max-w-[150px] cursor-help" title={session.trigger_detail}>
                                    {session.trigger_detail}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs max-w-xs break-words">{session.trigger_detail}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>

                        {/* Coluna Contato/Visitante */}
                        <TableCell className="font-medium text-sm">
                          {session.contact_name || (
                            <span className="font-mono text-xs text-muted-foreground">
                              {session.id.slice(0, 12)}...
                            </span>
                          )}
                        </TableCell>

                        {/* Coluna Telefone */}
                        <TableCell className="text-sm">
                          {session.phone ? (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              {session.phone}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>

                        {/* Coluna Status */}
                        <TableCell>
                          <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                        </TableCell>

                        {/* Coluna Iniciado em */}
                        <TableCell className="text-sm whitespace-nowrap">
                          {format(new Date(session.started_at), "dd/MM/yy HH:mm", { locale: ptBR })}
                        </TableCell>

                        {/* Coluna Duração */}
                        <TableCell className="text-sm">
                          {calcDuration(session.started_at, session.completed_at)}
                        </TableCell>

                        {/* Coluna Último Node */}
                        <TableCell className="text-sm">
                          {session.current_node_id ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded cursor-help">
                                    {session.current_node_id.slice(0, 8)}
                                  </code>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="font-mono text-xs">{session.current_node_id}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>

                        {/* Coluna Variáveis */}
                        <TableCell className="min-w-[220px]">
                          {context && Object.keys(context).length > 0 ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded truncate block cursor-help max-w-[200px]">
                                    {JSON.stringify(context).slice(0, 50)}
                                    {JSON.stringify(context).length > 50 ? '...' : ''}
                                  </code>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-sm">
                                  <pre className="text-xs whitespace-pre-wrap">
                                    {JSON.stringify(context, null, 2)}
                                  </pre>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>

                        {/* Coluna Logs */}
                        <TableCell>
                          {session.source === 'whatsapp' ? (
                            <Button
                              variant="outline"
                              size="xs"
                              className="gap-1 text-xs"
                              onClick={() => setLogsSessionId(session.id)}
                            >
                              <FileText className="h-3 w-3" />
                              Ver logs
                            </Button>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="outline" size="xs" className="gap-1 text-xs" disabled>
                                    <FileText className="h-3 w-3" />
                                    Ver logs
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Logs não disponíveis para sessões de link
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </TableCell>

                        {/* Coluna Chat — abre modal de conversa */}
                        <TableCell>
                          {session.conversation_id ? (
                            <Button
                              variant="outline"
                              size="xs"
                              className="gap-1 text-xs"
                              onClick={() => setChatConversationId(session.conversation_id)}
                            >
                              <MessageCircle className="h-3 w-3" />
                              Ver chat
                            </Button>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="outline" size="xs" className="gap-1 text-xs" disabled>
                                    <MessageCircle className="h-3 w-3" />
                                    Ver chat
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Chat não disponível para sessões de link
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </TableCell>

                        {/* Colunas dinâmicas — resposta do usuário em cada node */}
                        {activeNodeColumns.map((col) => {
                          const response = stepResponses[col.nodeId];
                          return (
                            <TableCell key={col.nodeId} className="text-sm">
                              {response ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="truncate block max-w-[140px] cursor-help" title={response}>
                                        {response}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs max-w-xs break-words">{response}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
          </div>
        </ScrollIndicators>

        {/* Paginação server-side */}
        <div className="px-4 border-t">
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      </div>

      {/* Modal de logs da sessão */}
      <FlowSessionLogsModal
        executionId={logsSessionId}
        open={!!logsSessionId}
        onOpenChange={(open) => {
          if (!open) setLogsSessionId(null);
        }}
      />

      {/* Modal de chat da conversa */}
      <ChatConversationModal
        conversationId={chatConversationId}
        open={!!chatConversationId}
        onOpenChange={(open) => {
          if (!open) setChatConversationId(null);
        }}
      />
    </div>
  );
}
